/**
 * Solver: backtracking sobre celdas con máscaras de candidatos y propagación
 * (singles desnudos, singles escondidos por fila y columna, y filtros de pista), con
 * corte al encontrar la solución número `limit`.
 *
 * Los filtros de pista solo tienen que ser **correctos** (no descartar nunca una
 * solución válida), no completos: cada rejilla completa se verifica al final contra
 * `allCluesHold`, así que un filtro flojo cuesta tiempo pero no puede inventar unicidad.
 */

import { allCluesHold } from './clues';
import { parseClue } from './clues';
import type { ParsedClue } from './clues';
import { isLatinSquare } from './grid';
import type { Clue, Grid } from './types';

/** Máscaras de candidatos, una por celda, indexadas `persona * n + hora`. */
type Candidates = Uint32Array;

interface Ctx {
  changed: boolean;
}

function popcount(mask: number): number {
  let m = mask;
  let count = 0;
  while (m !== 0) {
    m &= m - 1;
    count += 1;
  }
  return count;
}

function lowestBitIndex(mask: number): number {
  return 31 - Math.clz32(mask & -mask);
}

function setMask(cand: Candidates, i: number, next: number, ctx: Ctx): boolean {
  if (next === cand[i]) return next !== 0;
  cand[i] = next;
  ctx.changed = true;
  return next !== 0;
}

/** Horas en las que `person` puede estar en `room`, como máscara sobre horas. */
function hourMask(cand: Candidates, person: number, room: number, n: number): number {
  const bit = 1 << room;
  let mask = 0;
  for (let h = 0; h < n; h += 1) {
    if (((cand[person * n + h] as number) & bit) !== 0) mask |= 1 << h;
  }
  return mask;
}

function applyClueFilter(
  c: ParsedClue,
  cand: Candidates,
  n: number,
  ctx: Ctx,
): boolean {
  // Ojo: los filtros de igualdad **intersecan**, no sobrescriben. Sobrescribir
  // reintroduciría candidatos que otra regla ya había descartado y el punto fijo
  // oscilaría para siempre.
  switch (c.kind) {
    case 'AT': {
      const i = c.person * n + c.hour;
      return setMask(cand, i, (cand[i] as number) & (1 << c.room), ctx);
    }
    case 'NOT_AT': {
      const i = c.person * n + c.hour;
      return setMask(cand, i, (cand[i] as number) & ~(1 << c.room), ctx);
    }
    // Bajo reglas latinas cada sala se ocupa a todas las horas, así que la primera
    // hora ocupada de cualquier sala es la 0: `FIRST_IN` colapsa en `OPEN`, y `LAST_IN`
    // en `CLOSE` (MVP §3).
    case 'OPEN':
    case 'FIRST_IN': {
      const i = c.person * n + 0;
      return setMask(cand, i, (cand[i] as number) & (1 << c.room), ctx);
    }
    case 'CLOSE':
    case 'LAST_IN': {
      const i = c.person * n + (n - 1);
      return setMask(cand, i, (cand[i] as number) & (1 << c.room), ctx);
    }
    case 'BEFORE': {
      const hoursFirst = hourMask(cand, c.first, c.room, n);
      const hoursSecond = hourMask(cand, c.second, c.room, n);
      if (hoursFirst === 0 || hoursSecond === 0) return false;
      const maxSecond = 31 - Math.clz32(hoursSecond);
      const minFirst = lowestBitIndex(hoursFirst);
      // El primero tiene que caber antes de la última hora posible del segundo, y el
      // segundo después de la primera hora posible del primero.
      for (let h = maxSecond; h < n; h += 1) {
        const i = c.first * n + h;
        if (!setMask(cand, i, (cand[i] as number) & ~(1 << c.room), ctx)) return false;
      }
      for (let h = 0; h <= minFirst; h += 1) {
        const i = c.second * n + h;
        if (!setMask(cand, i, (cand[i] as number) & ~(1 << c.room), ctx)) return false;
      }
      return true;
    }
    case 'JUST_BEFORE': {
      const hoursFirst = hourMask(cand, c.first, c.room, n);
      const hoursSecond = hourMask(cand, c.second, c.room, n);
      if (hoursFirst === 0 || hoursSecond === 0) return false;
      const allowedFirst = hoursFirst & (hoursSecond >>> 1);
      const allowedSecond = hoursSecond & ((hoursFirst << 1) & ((1 << n) - 1));
      if (allowedFirst === 0 || allowedSecond === 0) return false;
      for (let h = 0; h < n; h += 1) {
        if ((allowedFirst & (1 << h)) === 0) {
          const i = c.first * n + h;
          if (!setMask(cand, i, (cand[i] as number) & ~(1 << c.room), ctx))
            return false;
        }
        if ((allowedSecond & (1 << h)) === 0) {
          const i = c.second * n + h;
          if (!setMask(cand, i, (cand[i] as number) & ~(1 << c.room), ctx))
            return false;
        }
      }
      return true;
    }
  }
}

/** Propaga hasta punto fijo. `false` = contradicción. */
function propagate(cand: Candidates, clues: readonly ParsedClue[], n: number): boolean {
  const ctx: Ctx = { changed: true };

  while (ctx.changed) {
    ctx.changed = false;

    for (const c of clues) {
      if (!applyClueFilter(c, cand, n, ctx)) return false;
    }

    // Single desnudo: celda con un solo candidato → esa sala sale de su fila y su columna.
    for (let p = 0; p < n; p += 1) {
      for (let h = 0; h < n; h += 1) {
        const mask = cand[p * n + h] as number;
        if (mask === 0) return false;
        if (popcount(mask) !== 1) continue;
        for (let other = 0; other < n; other += 1) {
          if (other !== h) {
            const i = p * n + other;
            if (!setMask(cand, i, (cand[i] as number) & ~mask, ctx)) return false;
          }
          if (other !== p) {
            const i = other * n + h;
            if (!setMask(cand, i, (cand[i] as number) & ~mask, ctx)) return false;
          }
        }
      }
    }

    // Single escondido: si una sala solo cabe en una celda de la fila (o de la columna),
    // esa celda es suya. Si no cabe en ninguna, la rama está muerta.
    for (let r = 0; r < n; r += 1) {
      const bit = 1 << r;
      for (let p = 0; p < n; p += 1) {
        let count = 0;
        let last = -1;
        for (let h = 0; h < n; h += 1) {
          if (((cand[p * n + h] as number) & bit) !== 0) {
            count += 1;
            last = h;
          }
        }
        if (count === 0) return false;
        if (count === 1 && !setMask(cand, p * n + last, bit, ctx)) return false;
      }
      for (let h = 0; h < n; h += 1) {
        let count = 0;
        let last = -1;
        for (let p = 0; p < n; p += 1) {
          if (((cand[p * n + h] as number) & bit) !== 0) {
            count += 1;
            last = p;
          }
        }
        if (count === 0) return false;
        if (count === 1 && !setMask(cand, last * n + h, bit, ctx)) return false;
      }
    }
  }
  return true;
}

function toGrid(cand: Candidates, n: number): Grid {
  const grid: Grid = [];
  for (let p = 0; p < n; p += 1) {
    const row = new Array<number>(n);
    for (let h = 0; h < n; h += 1) row[h] = lowestBitIndex(cand[p * n + h] as number);
    grid.push(row);
  }
  return grid;
}

function parseAll(clues: readonly Clue[], n: number): ParsedClue[] {
  return clues.map((c) => parseClue(c, n));
}

function initialCandidates(n: number): Candidates {
  const cand = new Uint32Array(n * n);
  cand.fill((1 << n) - 1);
  return cand;
}

function search(
  cand: Candidates,
  parsed: readonly ParsedClue[],
  original: readonly Clue[],
  n: number,
  limit: number,
  out: Grid[],
): void {
  if (out.length >= limit) return;
  if (!propagate(cand, parsed, n)) return;

  // MRV: se ramifica por la celda con menos candidatos.
  let bestIndex = -1;
  let bestCount = Number.POSITIVE_INFINITY;
  for (let i = 0; i < n * n; i += 1) {
    const count = popcount(cand[i] as number);
    if (count > 1 && count < bestCount) {
      bestCount = count;
      bestIndex = i;
      if (count === 2) break;
    }
  }

  if (bestIndex === -1) {
    const grid = toGrid(cand, n);
    // Red de seguridad: la propagación es correcta, pero una rejilla completa solo
    // cuenta si de verdad es un cuadrado latino y satisface todas las pistas.
    if (isLatinSquare(grid) && allCluesHold(original, grid)) out.push(grid);
    return;
  }

  const mask = cand[bestIndex] as number;
  for (let r = 0; r < n; r += 1) {
    if ((mask & (1 << r)) === 0) continue;
    const branch = cand.slice();
    branch[bestIndex] = 1 << r;
    search(branch, parsed, original, n, limit, out);
    if (out.length >= limit) return;
  }
}

/**
 * Soluciones de un caso, como mucho `limit`. Con `limit = 2` basta para decidir
 * unicidad: en cuanto aparece la segunda, se corta.
 */
export function findSolutions(clues: readonly Clue[], n: number, limit = 2): Grid[] {
  if (!Number.isInteger(n) || n < 1) throw new RangeError(`N inválido: ${n}`);
  if (limit < 1) throw new RangeError(`limit inválido: ${limit}`);
  const out: Grid[] = [];
  search(initialCandidates(n), parseAll(clues, n), clues, n, limit, out);
  return out;
}

/**
 * Número de soluciones, **saturado en `limit`** (2 por defecto): un resultado de 2
 * significa "dos o más". Un caso es válido si esto devuelve exactamente 1.
 */
export function countSolutions(clues: readonly Clue[], n: number, limit = 2): number {
  return findSolutions(clues, n, limit).length;
}

export interface PropagationResult {
  /** `true` si la propagación sola, sin ramificar, deja la rejilla completa. */
  solved: boolean;
  /** Contradicción detectada sin ramificar. */
  contradiction: boolean;
  grid: Grid | null;
}

/**
 * Solver de solo propagación: es el que decide `needsGuessing` para la dificultad.
 * No ramifica nunca, así que "no resuelto" significa "hay que adivinar", no "sin solución".
 */
export function solveByPropagation(
  clues: readonly Clue[],
  n: number,
): PropagationResult {
  const cand = initialCandidates(n);
  if (!propagate(cand, parseAll(clues, n), n)) {
    return { solved: false, contradiction: true, grid: null };
  }
  for (let i = 0; i < n * n; i += 1) {
    if (popcount(cand[i] as number) !== 1) {
      return { solved: false, contradiction: false, grid: null };
    }
  }
  return { solved: true, contradiction: false, grid: toGrid(cand, n) };
}
